import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import Select from 'react-select';
import './Forms.css';
import { FaPlus, FaEdit, FaTrashAlt, FaCopy, FaShareAlt, FaClock} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import AlertModal from '../common/AlertModal';
import ScheduleModal from '../common/ScheduleModal';

function Forms() {
  const [activeTab, setActiveTab] = useState('create');
  const [documents, setDocuments] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [types, setTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '', type: 'info' });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [schedules, setSchedules] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const dataContainerRef = useRef(null);
  const { currentUser } = useAuth();
  const [locations, setLocations] = useState([]);

  // Fetch documents, locations, types, and groups
  const fetchData = async () => {
    try {
      console.log('Fetching data...');

      // Fetch data
      const [docsSnap, locSnap, groupsSnap] = await Promise.all([
        getDocs(collection(db, 'templates')),
        getDocs(collection(db, 'locations')),
        getDocs(collection(db, 'groups'))
      ]);

      // Get user data
      const userDoc = await getDocs(collection(db, 'users')).then(snapshot => 
        snapshot.docs.find(doc => doc.data().uid === currentUser.uid)
      );
      
      const docsData = docsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter documents based on tab and status
      const createTemplates = docsData.filter(doc => !doc.isShared);
      const sharedTemplates = docsData.filter(doc => doc.isShared && doc.status === 'active');

      setDocuments(createTemplates);
      setMyTemplates(sharedTemplates);
      
      // Map locations data
      const locationsList = locSnap.docs
        .map(doc => ({
          value: doc.id,
          label: doc.data().name,
          status: doc.data().status,
          ...doc.data()
        }))
        .filter(location => location.status === 'active');
      setLocations(locationsList);

      // Map groups data
      const groupsList = groupsSnap.docs
        .map(doc => {
          const data = doc.data();
          return {
            value: doc.id,
            label: data.name,
            status: data.status,
            ...data
          };
        })
        .filter(group => group.status === 'active');
      setGroups(groupsList);

      // Get user's assigned groups
      const userData = userDoc?.data();
      setUserGroups(userData?.assignedGroups || []);

    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Add real-time listener for types
  useEffect(() => {
    console.log('Setting up types listener...');
    const typesRef = collection(db, 'types');
    const unsubscribe = onSnapshot(typesRef, (snapshot) => {
      console.log('Types collection changed!');
      console.log('Changes:', snapshot.docChanges().map(change => ({
        type: change.type,
        id: change.doc.id,
        data: change.doc.data()
      })));
      const typesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Updated types list:', typesList);
      setTypes(typesList);
    }, (error) => {
      console.error('Error in types listener:', error);
    });

    // Cleanup subscription
    return () => {
      console.log('Cleaning up types listener...');
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      if (location.state.newTemplateId) {
        fetchData();
      }
      // Show alert modal if message exists in state
      if (location.state.message) {
        setAlertModal({
          isOpen: true,
          message: location.state.message,
          type: location.state.type || 'info'
        });
      }
    }
  }, [location.state]);

  // Scroll to top when new template is created
  useEffect(() => {
    if (location.state?.scrollToTop && dataContainerRef.current) {
      dataContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.state?.scrollToTop]);

  // Handle template creation event
  useEffect(() => {
    const handleTemplateCreated = (event) => {
      const newTemplate = event.detail;
      setDocuments(prevDocs => [newTemplate, ...prevDocs]);
    };

    window.addEventListener('templateCreated', handleTemplateCreated);
    return () => window.removeEventListener('templateCreated', handleTemplateCreated);
  }, []);

  const handleEdit = (docId) => {
    navigate(`/forms/edit/${docId}`);
  };

  const handleDelete = async (docId) => {
    setPendingDelete(docId);
    setAlertModal({
      isOpen: true,
      message: 'Are you sure you want to delete this template?',
      type: 'confirm'
    });
  };

  const handleDeleteConfirm = async (confirmed) => {
    if (confirmed && pendingDelete) {
      try {
        await deleteDoc(doc(db, 'templates', pendingDelete));
        setDocuments(documents.filter(doc => doc.id !== pendingDelete));
        setMyTemplates(myTemplates.filter(doc => doc.id !== pendingDelete));
        setAlertModal({
          isOpen: true,
          message: 'Template deleted successfully',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting document:', error);
        setAlertModal({
          isOpen: true,
          message: 'Error deleting template. Please try again.',
          type: 'error'
        });
      }
    }
    setPendingDelete(null);
  };

  const handleCopy = async (template) => {
    try {
      const now = new Date();
      const copyData = {
        ...template,
        id: undefined,
        name: `${template.name} (Copy)`,
        createdAt: now,
        lastModified: now
      };

      // Remove Firestore-specific fields
      delete copyData.id;

      const docRef = await addDoc(collection(db, 'templates'), {
        ...copyData,
        createdAt: now,
        lastModified: now
      });

      const newTemplate = {
        ...copyData,
        id: docRef.id
      };

      // Insert the copy right after the original
      setDocuments(prevDocs => {
        const index = prevDocs.findIndex(doc => doc.id === template.id);
        const newDocs = [...prevDocs];
        newDocs.splice(index + 1, 0, newTemplate);
        return newDocs;
      });

      setAlertModal({
        isOpen: true,
        message: 'Template copied successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error copying template:', error);
      setAlertModal({
        isOpen: true,
        message: 'Error copying template. Please try again.',
        type: 'error'
      });
    }
  };

  const handleShare = async (template) => {
    try {
      // Validate that groups are assigned before sharing
      if (!template.assignedGroups || template.assignedGroups.length === 0) {
        setAlertModal({
          isOpen: true,
          message: 'Please assign at least one group before sharing the template',
          type: 'error'
        });
        return;
      }
  
      // Create a deep copy of the template and generate new IDs for all items
      const sharedTemplate = {
        ...template,
        items: template.items?.map(item => ({
          ...item,
          id: crypto.randomUUID(),
        })) || [],
        sharedAt: serverTimestamp(),
        sharedBy: currentUser.uid,
        isShared: true,
        status: 'active',
        originalTemplateId: template.id,
        templateVersion: 1,
        assignedGroups: template.assignedGroups.map(group => ({
          id: group.id,
          name: group.name
        }))
      };
  
      // Remove Firestore-specific fields
      delete sharedTemplate.id;
  
      const docRef = await addDoc(collection(db, 'templates'), {
        ...sharedTemplate,
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp()
      });
  
      // Update the UI with the new template
      const newTemplate = {
        ...sharedTemplate,
        id: docRef.id,
        createdAt: new Date(),
        lastModified: new Date()
      };
      
      setMyTemplates(prev => [newTemplate, ...prev]);
  
      setAlertModal({
        isOpen: true,
        message: 'Template has been shared to My Templates',
        type: 'success'
      });
    } catch (error) {
      console.error('Error sharing template:', error);
      setAlertModal({
        isOpen: true,
        message: 'Error sharing template. Please try again.',
        type: 'error'
      });
    }
  };
  

  const handleTypeChange = async (document, selectedOption) => {
    try {
      const docRef = doc(db, 'templates', document.id);
      const selectedType = selectedOption ? {
        id: selectedOption.value,
        name: selectedOption.label
      } : null;

      await updateDoc(docRef, { 
        type: selectedType,
        updatedAt: new Date().toISOString()
      });

      // Update both documents and myTemplates states
      if (document.isShared) {
        setMyTemplates(myTemplates.map(doc => {
          if (doc.id === document.id) {
            return { ...doc, type: selectedType };
          }
          return doc;
        }));
      } else {
        setDocuments(documents.map(doc => {
          if (doc.id === document.id) {
            return { ...doc, type: selectedType };
          }
          return doc;
        }));
      }
    } catch (error) {
      console.error('Error updating document type:', error);
    }
  };

  const handleGroupChange = async (document, selectedOptions) => {
    try {
      const docRef = doc(db, 'templates', document.id);
      const selectedGroups = selectedOptions.map(option => ({
        id: option.value,
        name: option.label
      }));

      await updateDoc(docRef, { 
        assignedGroups: selectedGroups,
        updatedAt: new Date().toISOString()
      });

      // Update both documents and myTemplates states
      if (document.isShared) {
        setMyTemplates(myTemplates.map(doc => {
          if (doc.id === document.id) {
            return { ...doc, assignedGroups: selectedGroups };
          }
          return doc;
        }));
      } else {
        setDocuments(documents.map(doc => {
          if (doc.id === document.id) {
            return { ...doc, assignedGroups: selectedGroups };
          }
          return doc;
        }));
      }
    } catch (error) {
      console.error('Error updating document groups:', error);
    }
  };

  const handleLocationChange = async (document, selectedOptions) => {
    try {
      const docRef = doc(db, 'templates', document.id);
      const selectedLocations = selectedOptions.map(option => ({
        id: option.value,
        name: option.label
      }));

      await updateDoc(docRef, { 
        assignedLocations: selectedLocations,
        updatedAt: new Date().toISOString()
      });

      // Update both documents and myTemplates states
      if (document.isShared) {
        setMyTemplates(myTemplates.map(doc => {
          if (doc.id === document.id) {
            return { ...doc, assignedLocations: selectedLocations };
          }
          return doc;
        }));
      } else {
        setDocuments(documents.map(doc => {
          if (doc.id === document.id) {
            return { ...doc, assignedLocations: selectedLocations };
          }
          return doc;
        }));
      }
    } catch (error) {
      console.error('Error updating document locations:', error);
    }
  };

  const handleNameEdit = (document, field) => {
    setEditingField({ docId: document.id, field });
    setEditingValue(document[field]);
  };

  const handleNameSubmit = async (document, field) => {
    try {
      const docRef = doc(db, 'templates', document.id);
      await updateDoc(docRef, { 
        [field]: editingValue,
        updatedAt: new Date().toISOString()
      });

      setDocuments(documents.map(doc => {
        if (doc.id === document.id) {
          return { ...doc, [field]: editingValue };
        }
        return doc;
      }));

      setEditingField(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error updating document name:', error);
    }
  };

  const handleScheduleUpdate = async (templateId, schedule) => {
    try {
      const templateRef = doc(db, 'templates', templateId);
      await updateDoc(templateRef, {
        schedule: {
          days: schedule.days,
          startDate: schedule.startDate,
          endDate: schedule.endDate || null
        }
      });

      setSchedules(prev => ({
        ...prev,
        [templateId]: schedule
      }));

      setAlertModal({
        isOpen: true,
        message: 'Schedule updated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating schedule:', error);
      setAlertModal({
        isOpen: true,
        message: 'Failed to update schedule',
        type: 'error'
      });
    }
  };

  const handleSendNow = async (template) => {
    try {
      console.log('========== Template Data Transfer Debugging ==========');
      console.log('1. Original template full data:', template);
      console.log('2. Template styling:', {
        templateStyles: template.templateStyles,
        fontSizes: template.fontSizes,
        customStyles: template.customStyles
      });
      console.log('3. Template form structure:', {
        formFields: template.formFields,
        items: template.items,
        sections: template.sections
      });
      
      const formInstance = {
        ...template,
        name: template.name || '',
        description: template.description || '',
        formFields: template.formFields?.map(field => {
          console.log('4. Processing form field:', field);
          return {
            ...field,
            id: crypto.randomUUID(),
            value: ''
          };
        }) || [],
        items: template.items?.map(item => {
          console.log('5. Processing item:', item);
          return {
            ...item,
            id: crypto.randomUUID(),
            isCompleted: false,
            timestamp: new Date().toISOString()
          };
        }) || [],
        templateStyles: template.templateStyles || {
          container: {},
          header: {},
          title: {},
          description: {},
          field: {},
          label: {},
          input: {},
          textarea: {},
          button: {},
          submitButton: {}
        },
        fontSizes: template.fontSizes || {
          section: 18,
          subsection: 16,
          item: 14
        },
        customStyles: template.customStyles || {},
        originalTemplateId: template.id,
        templateVersion: template.templateVersion || 1,
        status: 'in-progress',
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        userId: currentUser?.uid || '',
        locationId: template.assignedLocations?.[0]?.id || null,
        assignedLocations: template.assignedLocations || [],
        type: template.type || { name: 'default', id: '' }
      };

      // Remove any undefined or null values
      Object.keys(formInstance).forEach(key => {
        if (formInstance[key] === undefined || formInstance[key] === null) {
          delete formInstance[key];
          console.log(`6. Removed undefined/null field: ${key}`);
        }
      });

      delete formInstance.id;
      delete formInstance.schedule;

      console.log('7. Final form instance to be saved:', {
        templateStyles: formInstance.templateStyles,
        fontSizes: formInstance.fontSizes,
        customStyles: formInstance.customStyles,
        formFields: formInstance.formFields,
        items: formInstance.items
      });

      const docRef = await addDoc(collection(db, 'forms'), formInstance);
      console.log('8. Successfully saved form with ID:', docRef.id);
      
      // Verify the saved data
      const savedDoc = await getDoc(doc(db, 'forms', docRef.id));
      console.log('9. Verified saved form data:', savedDoc.data());
      
      setAlertModal({
        isOpen: true,
        message: 'Template successfully sent to Active Forms',
        type: 'success'
      });
    } catch (error) {
      console.error('10. Error sending template:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setAlertModal({
        isOpen: true,
        message: 'Failed to send template to Active Forms',
        type: 'error'
      });
    }
  };

  const createScheduledForms = async () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });

    const templatesSnapshot = await getDocs(collection(db, 'templates'));
    
    for (const doc of templatesSnapshot.docs) {
      const template = { id: doc.id, ...doc.data() };
      const schedule = template.schedule || { days: [], startDate: null, endDate: null };

      // Check if today is within the schedule date range
      if (schedule.startDate && schedule.days.includes(dayOfWeek)) {
        const startDate = new Date(schedule.startDate);
        const endDate = schedule.endDate ? new Date(schedule.endDate) : null;
        
        if (now >= startDate && (!endDate || now <= endDate)) {
          // Create a new form based on the template
          const newForm = {
            ...template,
            createdAt: serverTimestamp(),
            lastModified: serverTimestamp(),
            originalTemplateId: template.id,
            status: 'in-progress',
            assignedGroups: template.assignedGroups || [],
            assignedLocations: template.assignedLocations || [],
            items: template.items?.map(item => ({
              ...item,
              id: crypto.randomUUID(),
              isCompleted: false,
              timestamp: new Date().toISOString()
            })) || []
          };
          delete newForm.id;
          delete newForm.schedule;

          try {
            await addDoc(collection(db, 'documents'), newForm);
          } catch (error) {
            console.error('Error creating scheduled form:', error);
          }
        }
      }
    }
  };

  useEffect(() => {
    const checkSchedule = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        createScheduledForms();
      }
    };

    const interval = setInterval(checkSchedule, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const renderTypeSelect = (document) => {
    const documentType = document.type;
    const value = documentType ? {
      value: documentType.id,
      label: documentType.name
    } : null;

    // Convert types to the format needed for react-select
    const typeOptions = types
      .filter(type => type.status === 'active')
      .map(type => ({
        value: type.id,
        label: type.name
      }));
    
    console.log('Type options for dropdown:', typeOptions);
  
    return (
      <Select
        value={value}
        onChange={(selectedOption) => handleTypeChange(document, selectedOption)}
        options={typeOptions}
        className="group-select"
        classNamePrefix="group-select"
        placeholder="Select type..."
        isClearable={true}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 })
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#2196F3',
            primary75: '#2196F3cc',
            primary50: '#2196F380',
            primary25: '#2196F340',
            danger: '#f44336',
            dangerLight: '#f4433640',
            neutral0: '#011029',
            neutral5: '#011029',
            neutral10: '#011029',
            neutral20: 'rgba(255, 255, 255, 0.2)',
            neutral30: 'rgba(255, 255, 255, 0.3)',
            neutral40: 'rgba(255, 255, 255, 0.4)',
            neutral50: 'rgba(255, 255, 255, 0.5)',
            neutral60: 'rgba(255, 255, 255, 0.6)',
            neutral70: 'rgba(255, 255, 255, 0.7)',
            neutral80: 'rgba(255, 255, 255, 0.8)',
            neutral90: 'rgba(255, 255, 255, 0.9)'
          }
        })}
      />
    );
  };

  const renderGroupSelect = (document) => {
    const documentGroups = document.assignedGroups || [];
    const value = documentGroups.map(group => ({
      value: group.id,
      label: group.name
    }));
  
    return (
      <Select
        isMulti
        value={value}
        onChange={(selectedOptions) => handleGroupChange(document, selectedOptions)}
        options={groups}
        className="group-select"
        classNamePrefix="group-select"
        placeholder="Select groups..."
        isClearable={true}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 })
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#2196F3',
            primary75: '#2196F3cc',
            primary50: '#2196F380',
            primary25: '#2196F340',
            danger: '#f44336',
            dangerLight: '#f4433640',
            neutral0: '#011029',
            neutral5: '#011029',
            neutral10: '#011029',
            neutral20: 'rgba(255, 255, 255, 0.2)',
            neutral30: 'rgba(255, 255, 255, 0.3)',
            neutral40: 'rgba(255, 255, 255, 0.4)',
            neutral50: 'rgba(255, 255, 255, 0.5)',
            neutral60: 'rgba(255, 255, 255, 0.6)',
            neutral70: 'rgba(255, 255, 255, 0.7)',
            neutral80: 'rgba(255, 255, 255, 0.8)',
            neutral90: 'rgba(255, 255, 255, 0.9)'
          }
        })}
      />
    );
  };

  const renderLocationSelect = (document) => {
    const documentLocations = document.assignedLocations || [];
    const value = documentLocations.map(location => ({
      value: location.id,
      label: location.name
    }));

    return (
      <Select
        isMulti
        value={value}
        onChange={(selectedOptions) => handleLocationChange(document, selectedOptions || [])}
        options={locations}
        className="location-select"
        classNamePrefix="location-select"
        placeholder="Select locations..."
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 })
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#2196F3',
            primary75: '#2196F3cc',
            primary50: '#2196F380',
            primary25: '#2196F340',
            danger: '#f44336',
            dangerLight: '#f4433640',
            neutral0: '#011029',
            neutral5: '#011029',
            neutral10: '#011029',
            neutral20: 'rgba(255, 255, 255, 0.2)',
            neutral30: 'rgba(255, 255, 255, 0.3)',
            neutral40: 'rgba(255, 255, 255, 0.4)',
            neutral50: 'rgba(255, 255, 255, 0.5)',
            neutral60: 'rgba(255, 255, 255, 0.6)',
            neutral70: 'rgba(255, 255, 255, 0.7)',
            neutral80: 'rgba(255, 255, 255, 0.8)',
            neutral90: 'rgba(255, 255, 255, 0.9)'
          }
        })}
      />
    );
  };

  const renderEditableCell = (document, field, value) => {
    const isEditing = editingField?.docId === document.id && editingField?.field === field;

    return isEditing ? (
      <input
        type="text"
        value={editingValue}
        onChange={(e) => setEditingValue(e.target.value)}
        onBlur={() => handleNameSubmit(document, field)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            handleNameSubmit(document, field);
          }
        }}
        autoFocus
        className="editable-input"
      />
    ) : (
      <span
        className="editable-cell"
        onClick={() => handleNameEdit(document, field)}
      >
        {value}
      </span>
    );
  };

  return (
    <div className="forms-container">
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Create Templates
        </button>
        <button 
          className={`tab ${activeTab === 'my' ? 'active' : ''}`}
          onClick={() => setActiveTab('my')}
        >
          My Templates
        </button>
      </div>

      <div className="content-area" ref={dataContainerRef}>
        {activeTab === 'create' && (
          <>
            <div className="button-area">
              <button 
                className="add-button"
                onClick={() => navigate('/forms/tasklists/create')}
              >
                <FaPlus /> Add Template
              </button>
            </div>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Groups</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '1rem' }}>
                        No templates found. Click the Add Template button to add a sample template.
                      </td>
                    </tr>
                  ) : (
                    documents.map((doc) => (
                      <tr key={doc.id}>
                        <td>{renderEditableCell(doc, 'name', doc.name)}</td>
                        <td>{renderEditableCell(doc, 'description', doc.description || '')}</td>
                        <td className="type-cell">{renderTypeSelect(doc)}</td>
                        <td className="group-cell">{renderGroupSelect(doc)}</td>
                        <td>
                          <div className="action-buttons">
                            <FaEdit 
                              className="edit-icon"
                              onClick={() => handleEdit(doc.id)}
                              title="Edit Template"
                            />
                            <FaCopy
                              className="copy-icon"
                              onClick={() => handleCopy(doc)}
                              title="Copy Template"
                            />
                            <FaShareAlt
                              className="share-icon"
                              onClick={() => handleShare(doc)}
                              title="Share to My Templates"
                            />
                            <FaTrashAlt 
                              className="delete-icon"
                              onClick={() => handleDelete(doc.id)}
                              title="Delete Template"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'my' && (
          <>
            <div className="button-area">
              <button 
                className="add-button"
                onClick={() => setActiveTab('create')}
              >
                <FaPlus /> Create New Template
              </button>
            </div>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Schedule</th>
                    <th>Type</th>
                    <th>Groups</th>
                    <th>Locations</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myTemplates.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>
                        No templates found.
                      </td>
                    </tr>
                  ) : (
                    myTemplates.map((doc) => (
                      <tr key={doc.id}>
                        <td>{renderEditableCell(doc, 'name', doc.name)}</td>
                        <td>{renderEditableCell(doc, 'description', doc.description)}</td>
                        <td>
                          <div className="schedule-actions">
                            <button 
                              className="schedule-button"
                              onClick={() => {
                                setSelectedTemplateId(doc.id);
                                setScheduleModalOpen(true);
                              }}
                              title="Set Schedule"
                            >
                              <FaClock />
                            </button>
                          </div>
                        </td>
                        <td className="type-cell">{renderTypeSelect(doc)}</td>
                        <td className="group-cell">{renderGroupSelect(doc)}</td>
                        <td className="location-cell">{renderLocationSelect(doc)}</td>
                        <td>
                          <div className="action-buttons">
                            <FaEdit 
                              className="edit-icon"
                              onClick={() => handleEdit(doc.id)}
                              title="Edit Template"
                            />
                            <FaCopy
                              className="copy-icon"
                              onClick={() => handleCopy(doc)}
                              title="Copy Template"
                            />
                            <FaTrashAlt 
                              className="delete-icon"
                              onClick={() => handleDelete(doc.id)}
                              title="Delete Template"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <ScheduleModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onSave={(selectedDays) => {
          handleScheduleUpdate(selectedTemplateId, selectedDays);
          setScheduleModalOpen(false);
        }}
        initialSchedule={selectedTemplateId ? (schedules[selectedTemplateId] || []) : []}
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={(confirmed) => {
          if (alertModal.type === 'confirm') {
            handleDeleteConfirm(confirmed);
          }
          setAlertModal({ isOpen: false, message: '', type: 'info' });
        }}
      />
    </div>
  );
}

export default Forms;